import * as React from "react";
import styled from "styled-components";

import { Text } from "src/library/components/Text";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { formatPrice } from "src/core/prices/utils/price-helpers";
import {
  TabbedNavigation,
  TabOptions
} from "src/library/components/TabbedNavigation/TabbedNavigation";
import { PageProps } from "src/pages/Router/routes";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { useTransactionDetails } from "src/core/accounting/hooks/useTransactionDetails";
import AccountingStatusBadge from "src/core/accounting/components/AccountingStatusBadge";
import RelatedInvoicesTab from "src/core/accounting/components/RelatedInvoicesTab";
import { Datetime } from "src/library/components/Datetime";

interface TransactionDetailsPageProps {
  transactionId: string;
}

const TransactionDetailsPage: React.ReactFCWithChildren<PageProps<TransactionDetailsPageProps, { transactionId: string }>> = props => {
  const { match } = props;
  const transactionId = match?.params.transactionId ? parseInt(match.params.transactionId) : undefined;

  // Use branch-scoped access enforcement when in masquerade mode (customer viewing as specific drop)
  const {
    transaction, loading, error
  } = useTransactionDetails(transactionId);

  // Build tabs for TabbedNavigation
  const tabs: TabOptions[] = React.useMemo(() => {
    return [
      {
        title: "Related Invoice",
        name: "related-invoices",
        content: () => <RelatedInvoicesTab transactionId={transactionId} />
      }
    ];
  }, [ transactionId ]);

  if (loading) {
    return (
      <Layout {...props}>
        <MainContentWrapper>
          <LoadingSpinner type="circular" />
        </MainContentWrapper>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout {...props}>
        <MainContentWrapper>
          <Text color="danger">
            {`Error loading transaction: ${error.message}`}
          </Text>
        </MainContentWrapper>
      </Layout>
    );
  }

  return (
    <Layout {...props}>
      <MainContentWrapper>
        {transaction && (
          <>
            <PageHeader>
              <HeaderTop>
                <Text
                  fontSize="h1"
                  weight="bold"
                >
                  {`Transaction #${transaction.externalId || transaction.id}`}
                </Text>

                <AccountingStatusBadge
                  variant="transaction"
                  status={transaction.status}
                />
              </HeaderTop>

              <HeaderDetails>
                <Text
                  fontSize="p"
                  color="secondary"
                >
                  <Datetime
                    date={new Date(transaction.transactionDate)}
                    template="date_only"
                  />
                </Text>

                <Text
                  fontSize="h3"
                  weight="bold"
                >
                  {formatPrice(transaction.amount)}
                </Text>
              </HeaderDetails>
            </PageHeader>

            <TabbedNavigation
              tabs={tabs}
              defaultTab="related-invoices"
            />
          </>
        )}
      </MainContentWrapper>
    </Layout>
  );
};

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.margins.md};
  margin-bottom: ${({ theme }) => theme.spacing.margins.lg};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.md};
`;

const HeaderDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.lg};
`;

export default TransactionDetailsPage;
